'use client';

import { useEffect, useRef } from 'react';
import { gsap } from 'gsap';
import { ScrollTrigger } from 'gsap/ScrollTrigger';

// Register GSAP plugins
if (typeof window !== 'undefined') {
  gsap.registerPlugin(ScrollTrigger);
}

type ScrollColorTransitionProps = {
  heroId: string;
  backgroundColor: string;
};

export default function ScrollColorTransition({ heroId, backgroundColor }: ScrollColorTransitionProps) {
  const bgRef = useRef<HTMLDivElement | null>(null);

  useEffect(() => {
    const bgEl = bgRef.current;
    if (!bgEl) return;

    let rafId: number | null = null;
    let ctx: gsap.Context | null = null;
    let isMounted = true;

    const initWhenReady = () => {
      // Check if component is still mounted
      if (!isMounted) {
        return;
      }
      
      const triggerEl = document.getElementById(heroId);
      if (!triggerEl) {
        rafId = requestAnimationFrame(initWhenReady);
        return;
      }

      // Only create context if still mounted
      if (!isMounted) return;

      // Create GSAP context only when we have the trigger element
      ctx = gsap.context(() => {
        gsap.to(bgEl, {
          backgroundColor: '#FDFEF6',
          scrollTrigger: {
            trigger: triggerEl,
            start: 'top top',
            end: '+=300px',
            scrub: 0.5,
            markers: false,
          },
          ease: 'circ.out',
        });
      }, bgEl);
    };

    initWhenReady();

    return () => {
      isMounted = false;
      
      // Cancel any pending RAF first
      if (rafId !== null) {
        cancelAnimationFrame(rafId);
        rafId = null;
      }
      
      // Safely revert GSAP context if it was created
      if (ctx) {
        try {
          ctx.revert();
        } catch (error) {
          // Silently handle any cleanup errors
          console.warn('ScrollColorTransition cleanup error:', error);
        }
        ctx = null;
      }
    };
  }, [heroId]);

  return (
    <div
      ref={bgRef}
      aria-hidden="true"
      className="fixed inset-0 -z-10 pointer-events-none"
      style={{ backgroundColor }}
    />
  );
}

